import {
  createEffect,
  createMemo,
  mergeProps,
  createSignal,
  splitProps,
  onCleanup,
  Show,
  untrack,
  type Accessor,
  type JSX,
  onMount,
  type Setter,
  type ComponentProps,
} from "solid-js";
import { BaselineAligner } from "~/common/baseline_aligner";
import { Divider } from "~/common/divider";
import { containsThroughLayers, Floating, hasLayerAbove } from "~/common/floating";
import { Link } from "~/inertia";
import { type InertiaLinkProps } from "~/inertia/Link";
import { type FallbackPlacements, type Placement } from "~/util/html/floating";
import clsx from "~/util/html/style";
import { createScrollFade } from "~/util/scroll_fade";

export type PopoverProps = {
  placement?: Placement;
  fallbackPlacements?: FallbackPlacements;
  /** Offset distance from trigger (default: 8) */
  offset?: number;
  class?: string;
  enabled?: boolean;
  /** Mount the panel on first open, for a popover rendered once per table row or cell. */
  lazy?: boolean;
  renderTrigger: (trigger: TriggerProps) => JSX.Element;
  children: (content: ContentProps) => JSX.Element;
};

export type TriggerProps = {
  props: {
    ref: (el: HTMLElement | SVGElement) => void;
  };
  setOpen: Setter<boolean>;
  /** Whether the panel is open, for a trigger that reads differently while it is. */
  open: Accessor<boolean>;
};

export type ContentProps = {
  setOpen: Setter<boolean>;
};

export function Popover(_props: PopoverProps) {
  const props = mergeProps({ placement: "bottom" as const, enabled: true }, _props);
  const [isTriggered, setIsTriggered] = createSignal(false);
  const everOpened = createMemo(
    (opened) => opened || isTriggered(),
    untrack(() => !props.lazy),
  );

  // Unset when a caller never attaches the trigger ref; the document listeners run regardless.
  let triggerRef: HTMLElement | SVGElement | undefined;
  let floatingRef!: HTMLDivElement;

  onMount(() => {
    const isInside = (target: EventTarget | null) =>
      target instanceof Node &&
      (triggerRef?.contains(target) || containsThroughLayers(floatingRef, target));

    // Where the press began, not only where the click lands: picking from a panel opened inside this
    // one (a combobox list) hides that panel on mousedown, so the mouseup lands on whatever was
    // beneath it and the browser fires the click on an ancestor the two share, often <body>.
    let pressBeganInside = false;
    function handleMouseDown(event: MouseEvent) {
      pressBeganInside = isTriggered() && isInside(event.target);
    }

    function handleClickOutside(event: MouseEvent) {
      // A keyboard click (detail 0) has no press of its own, so an earlier one must not speak for it.
      const began = event.detail > 0 && pressBeganInside;
      pressBeganInside = false;
      if (!isTriggered() || began || isInside(event.target)) {
        return;
      }

      setIsTriggered(false);
    }

    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("click", handleClickOutside, true);

    onCleanup(() => {
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("click", handleClickOutside, true);
    });
  });

  const isOpen = () => isTriggered() && props.enabled;

  // Escape belongs to the innermost layer, and to any control that already handled it.
  createEffect(() => {
    if (!isOpen()) return;

    function handleEscape(event: KeyboardEvent) {
      if (
        event.key !== "Escape" ||
        event.isComposing ||
        event.defaultPrevented ||
        hasLayerAbove(floatingRef)
      )
        return;

      event.preventDefault();
      setIsTriggered(false);
      triggerRef?.focus();
    }

    document.addEventListener("keydown", handleEscape);
    onCleanup(() => document.removeEventListener("keydown", handleEscape));
  });

  return (
    <>
      {props.renderTrigger({
        props: {
          ref: (el) => {
            triggerRef = el;
          },
        },
        setOpen: setIsTriggered,
        open: isOpen,
      })}

      <Show when={everOpened()}>
        <Floating
          reference={() => triggerRef}
          open={isOpen}
          placement={props.placement}
          fallbackPlacements={props.fallbackPlacements}
          offset={props.offset}
          class={props.class}
          floatingRef={(element) => {
            floatingRef = element;
          }}
        >
          <Show when={isOpen()}>{props.children({ setOpen: setIsTriggered })}</Show>
        </Floating>
      </Show>
    </>
  );
}

/**
 * The panel portals into the top layer, but Solid's delegated click still walks the component
 * tree, so a click inside the panel would also fire the `onClick` of whatever row or card the
 * trigger sits in — expanding or opening the thing behind the menu. The panel swallows its own
 * clicks so no caller has to remember to; a caller's `onClick` still runs.
 */
export function PopoverContent(
  props: Omit<ComponentProps<"div">, "onClick"> & {
    onClick?: JSX.EventHandler<HTMLDivElement, MouseEvent>;
    /** A panel floating over dark chrome, as in the imaging viewer. */
    dark?: boolean;
  },
) {
  const [own, rest] = splitProps(props, ["dark", "class", "onClick"]);
  return (
    <div
      {...rest}
      data-dark={own.dark ? true : undefined}
      class={clsx(
        "overflow-hidden rounded-xl border border-brand-divider bg-white text-sm shadow-lg",
        "data-dark:border-brand-subtle-dark data-dark:bg-gray-800 data-dark:text-typography-primary-dark",
        own.class,
      )}
      onClick={(event) => {
        event.stopPropagation();
        own.onClick?.(event);
      }}
    />
  );
}

/**
 * Popover panel whose body scrolls, capped to the space available below the trigger
 * (published by the floating `size` middleware as `--floating-available-height`) so a
 * long list never overflows a short viewport/iframe with its bottom rows clipped and
 * unreachable. Falls back to `maxHeight` (24rem by default) outside a floating context.
 *
 * Swallows its own clicks for the same reason `PopoverContent` does.
 *
 * The chrome and the scrollport are separate elements because the house scroll fade is a mask,
 * and a mask clips its element's painting to the border box: put it on the chrome and the panel
 * loses its shadow the moment it grows long enough to scroll.
 */
export function ScrollablePopoverContent(props: {
  children: JSX.Element;
  class?: string;
  /** A CSS length, for a panel that earns more room than the default. */
  maxHeight?: string;
  /** Pinned above the scrolling body, such as the search a long list is filtered by. */
  header?: JSX.Element;
  /** Pinned under the scrolling body, so the actions that end a long list stay in view. */
  footer?: JSX.Element;
  /** False where the body's fade already sets the footer apart. */
  footerDivider?: boolean;
  /** Which edges of the body fade as it scrolls: its end, or both for a body read back from where it
   *  opens (a thread opened at its newest message). */
  fade?: "end" | "both";
  /** False for rows moved through from elsewhere, such as a search box's arrows: Chromium gives a
   *  scroller with no tabbable children a Tab stop of its own, and that stop has no name. */
  bodyTabStop?: boolean;
}) {
  const cap = () => props.maxHeight ?? "24rem";
  const maxHeight = () => `min(${cap()}, var(--floating-available-height, ${cap()}))`;

  let scrollRef: HTMLDivElement | undefined;
  // Static, like every other scroll-fade opt-in.
  createScrollFade(() => scrollRef, {
    positions: untrack(() => props.fade) === "both" ? ["top", "bottom"] : ["bottom"],
  });

  return (
    <div
      class="flex flex-col overflow-hidden rounded-lg border border-brand-subtle bg-white shadow-lg"
      style={{ "max-height": maxHeight() }}
      onClick={(event) => event.stopPropagation()}
    >
      <Show when={props.header}>
        <div class="shrink-0">{props.header}</div>
      </Show>
      <div
        ref={scrollRef}
        tabindex={props.bodyTabStop === false ? -1 : undefined}
        class={clsx("min-h-0 overflow-y-auto", props.class)}
        style={{ "max-height": maxHeight() }}
      >
        {props.children}
      </div>
      <Show when={props.footer}>
        <Show when={props.footerDivider !== false}>
          <Divider />
        </Show>
        <div class="shrink-0 py-1">{props.footer}</div>
      </Show>
    </div>
  );
}

/**
 * The row a popover's actions sit in, pinned to the bottom-right corner of the panel rather
 * than stretched across it by the panel's own flex column. Buttons stay the caller's (usually
 * `size="small"` and a quiet variant); the spacing above the row is the panel's column gap.
 */
export function PopoverFooterActions(props: { children: JSX.Element }) {
  return <div class="flex justify-end gap-2">{props.children}</div>;
}

/** Whether this key press confirms, taking it if so. Enter confirms, shift+Enter doesn't. A focused
 *  button or link is skipped: Enter is already how you press the one you tabbed to, so catching it
 *  here would turn Enter on Cancel into a confirm. */
export function takeConfirmingEnter(event: KeyboardEvent): boolean {
  if (event.key !== "Enter" || event.shiftKey || event.repeat) return false;
  if (event.target instanceof HTMLButtonElement || event.target instanceof HTMLAnchorElement)
    return false;

  event.preventDefault();
  return true;
}

export function PopoverConfirmContent(props: {
  children: JSX.Element;
  actions: JSX.Element;
  class?: string;
  width?: "md" | "lg";
  /** See `takeConfirmingEnter`. */
  onEnter?: () => void;
}) {
  const width = () => props.width ?? "md";
  return (
    <PopoverContent
      onKeyDown={(event) => {
        if (props.onEnter && takeConfirmingEnter(event)) props.onEnter();
      }}
      class={clsx(
        "flex flex-col gap-3 px-4 py-3",
        width() === "md" ? "w-80" : "w-128",
        props.class,
      )}
    >
      <div class="text-sm">{props.children}</div>
      <PopoverFooterActions>{props.actions}</PopoverFooterActions>
    </PopoverContent>
  );
}

export function PopoverItemButton(
  props: ComponentProps<"button"> & { variant?: "default" | "danger" },
) {
  return (
    <button
      type="button"
      {...props}
      class={clsx(
        "flex w-full cursor-pointer items-center gap-2 px-5 py-3 text-sm hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:bg-transparent",
        props.variant === "danger" && "text-brand-danger",
        props.class,
      )}
    />
  );
}

export function PopoverItemContent(props: {
  icon?: JSX.Element;
  label: string;
  description?: string;
  /** Two lines of the description, the rest on hover, for long catalog text */
  clampDescription?: boolean;
  variant?: "default" | "danger";
  class?: string;
}) {
  return (
    <div
      class={clsx(
        "grid w-full grid-cols-[auto_1fr] items-baseline gap-y-0.5 text-left",
        props.class,
      )}
    >
      <Show when={props.icon} fallback={<div />}>
        <BaselineAligner class="mr-2">
          <span class="shrink-0">{props.icon}</span>
        </BaselineAligner>
      </Show>
      <div
        class={clsx(
          "font-medium",
          props.variant === "danger" ? "text-brand-danger" : "text-typography-primary",
        )}
      >
        {props.label}
      </div>
      <Show when={props.description}>
        <div
          class={clsx(
            "col-start-2 text-xs text-typography-secondary",
            props.clampDescription && "line-clamp-2",
          )}
          title={props.clampDescription ? props.description : undefined}
        >
          {props.description}
        </div>
      </Show>
    </div>
  );
}

export function PopoverItemA(props: ComponentProps<"a"> & { variant?: "default" | "danger" }) {
  return (
    <a
      {...props}
      class={clsx(
        "flex w-full cursor-pointer items-center gap-2 px-5 py-3 text-sm hover:bg-brand-hover",
        props.variant === "danger" && "text-brand-danger",
        props.class,
      )}
    />
  );
}

export function PopoverItemLink<T extends keyof JSX.IntrinsicElements = "a">(
  props: InertiaLinkProps<T> & { variant?: "default" | "danger" },
) {
  return (
    <Link
      {...props}
      class={clsx(
        "flex w-full cursor-pointer items-center gap-2 px-5 py-3 text-sm hover:bg-brand-hover",
        props.variant === "danger" && "text-brand-danger",
        props.class,
      )}
    />
  );
}
